import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Link2, Link2Off, RefreshCw, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/utils';

function GoogleIcon(props) {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" {...props}>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.1 0 24 0 14.7 0 6.7 5.4 2.7 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.2 5.6c4.2-3.9 6.9-9.6 6.9-17.3z" />
      <path fill="#FBBC05" d="M10.6 28.6c-1-3-1-6.2 0-9.2l-7.9-6.1c-3.4 6.8-3.4 14.6 0 21.4l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.1 0 11.2-2 15-5.5l-7.2-5.6c-2 1.4-4.6 2.2-7.8 2.2-6.3 0-11.6-4.1-13.4-10l-7.9 6.1C6.7 42.6 14.7 48 24 48z" />
    </svg>
  );
}

function OutlookIcon(props) {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" {...props}>
      <path fill="#0078D4" d="M28 6h16v36H28V6z" />
      <path fill="#106EBE" d="M28 6h16v18H28V6z" />
      <path fill="#0078D4" d="M4 10h24v28H4V10z" />
      <path fill="#fff" d="M16 15c-4.4 0-8 4-8 9s3.6 9 8 9 8-4 8-9-3.6-9-8-9zm0 15c-2.8 0-5-2.7-5-6s2.2-6 5-6 5 2.7 5 6-2.2 6-5 6z" />
    </svg>
  );
}

const PROVIDERS = [
  { key: 'google', label: 'Google Calendar', icon: GoogleIcon, color: 'from-red-50 to-blue-50' },
  { key: 'microsoft', label: 'Microsoft Outlook', icon: OutlookIcon, color: 'from-blue-50 to-cyan-50' },
];

export default function ConnectedCalendars() {
  const [state, setState] = useState({ google: null, microsoft: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/integrations');
      setState(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const handler = (ev) => {
      if (ev.data === 'integration:connected' || ev.data === 'integration:error') {
        load();
        if (ev.data === 'integration:connected') toast.success('Calendar connected');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const connect = async (provider) => {
    if (!state[provider]?.configured) {
      toast.error(`${provider === 'google' ? 'Google' : 'Outlook'} integration is not yet configured. Contact your administrator.`);
      return;
    }
    setBusy(provider);
    try {
      const r = await api.get(`/integrations/${provider}/authorize`);
      const w = window.open(r.data.authorization_url, '_blank', 'width=520,height=680');
      if (!w) {
        window.location.href = r.data.authorization_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start connection');
    } finally {
      setBusy('');
    }
  };

  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${provider === 'google' ? 'Google Calendar' : 'Outlook Calendar'}? Existing external events remain but no new events will sync.`)) return;
    setBusy(provider);
    try {
      await api.post(`/integrations/${provider}/disconnect`);
      toast.success('Disconnected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy('');
    }
  };

  const toggleSync = async (provider, next) => {
    setBusy(provider);
    try {
      await api.patch(`/integrations/${provider}/preferences`, { sync_enabled: next });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusy('');
    }
  };

  const testSync = async () => {
    setBusy('test');
    try {
      const r = await api.post('/integrations/sync-test');
      const parts = [];
      if (r.data.google_synced) parts.push('Google ✓');
      if (r.data.microsoft_synced) parts.push('Outlook ✓');
      if (parts.length === 0) toast.error('No connected calendars accepted the sync test');
      else toast.success(`Test event created: ${parts.join(' · ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setBusy('');
    }
  };

  const syncNow = async (provider) => {
    setBusy('pull');
    try {
      const r = await api.post(`/integrations/${provider}/sync-now`);
      toast.success(`Synced from Google: ${r.data.created} new, ${r.data.updated} updated, ${r.data.deleted} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setBusy('');
    }
  };

  const anyConnected = !!state.google?.connection || !!state.microsoft?.connection;

  return (
    <Card data-testid="connected-calendars">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-magenta" /> Connected Calendars</CardTitle>
          {anyConnected && (
            <Button variant="outline" size="sm" onClick={testSync} disabled={busy === 'test'} data-testid="test-sync">
              <Zap className="w-3.5 h-3.5 mr-1.5" /> {busy === 'test' ? 'Testing…' : 'Send test event'}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">Two-way sync: events you create in the Hub appear in your connected calendars, and changes made in Google Calendar flow back into the Hub automatically every 5 minutes.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && PROVIDERS.map(({ key, label, icon: Icon, color }) => {
          const info = state[key] || {};
          const conn = info.connection;
          const isConfigured = info.configured;
          return (
            <div key={key} className={`rounded-xl border border-slate-200 bg-gradient-to-r ${color} p-4`} data-testid={`integration-${key}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-white shadow-sm flex items-center justify-center">
                    <Icon />
                  </div>
                  <div>
                    <div className="font-semibold text-navy text-sm flex items-center gap-2">
                      {label}
                      {conn ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>
                      ) : !isConfigured ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0"><AlertTriangle className="w-3 h-3 mr-1" /> Not configured</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0">Not connected</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {conn ? (
                        <>
                          Connected as <span className="font-medium text-navy">{conn.email || 'unknown'}</span> · {fmtDate(conn.connected_at)}
                          {conn.last_synced_at && <> · Last sync {new Date(conn.last_synced_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>}
                        </>
                      ) : !isConfigured ? (
                        'Waiting for administrator to add OAuth credentials.'
                      ) : (
                        'Click Connect to authorize AAWTA Partnership Hub.'
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {conn && (
                    <>
                      <div className="flex items-center gap-1.5 mr-1">
                        <Switch
                          checked={!!conn.sync_enabled}
                          onCheckedChange={(v) => toggleSync(key, v)}
                          disabled={busy === key}
                          data-testid={`sync-${key}`}
                        />
                        <span className="text-xs text-slate-600 font-medium">Sync</span>
                      </div>
                      {key === 'google' && (
                        <Button variant="outline" size="sm" onClick={() => syncNow(key)} disabled={busy === 'pull'} data-testid={`sync-now-${key}`}>
                          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy === 'pull' ? 'animate-spin' : ''}`} /> {busy === 'pull' ? 'Syncing…' : 'Sync now'}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => connect(key)} disabled={busy === key} data-testid={`reconnect-${key}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconnect
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => disconnect(key)} disabled={busy === key} className="text-rose-600 hover:bg-rose-50" data-testid={`disconnect-${key}`}>
                        <Link2Off className="w-3.5 h-3.5 mr-1" /> Disconnect
                      </Button>
                    </>
                  )}
                  {!conn && (
                    <Button
                      onClick={() => connect(key)}
                      disabled={busy === key || !isConfigured}
                      className="bg-magenta hover:bg-magenta/90 text-white"
                      data-testid={`connect-${key}`}
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1" /> Connect {key === 'google' ? 'Google' : 'Outlook'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
